import { usePropertyPhone } from '@vibesites/runtime/react';
import { pages as basePages } from '@vibesites/theme-quinn-residences/pages';
import pagesData from '../pages.json';
import { clean, formatPhone, num } from './quinn';
import { useCustomField, useRawProperty } from './hooks';
import { gatherTours } from './tours';

interface PageEntry {
  path?: string;
  visible?: boolean;
  template?: string;
  deleted_at?: string | null;
}

/** Local pages.json only holds overrides; layer it over the parent theme's pages by alias. */
const localPages = pagesData as unknown as Record<string, PageEntry>;
const PAGES: Record<string, PageEntry> = { ...(basePages as Record<string, PageEntry>) };
for (const [alias, entry] of Object.entries(localPages)) PAGES[alias] = { ...PAGES[alias], ...entry };

/** Site path for a page alias, or null when the page is hidden or absent. */
export function pagePath(alias: string): string | null {
  const p = PAGES[alias];
  if (!p || p.visible === false || p.deleted_at || p.template) return null;
  return p.path ?? `/${alias}`;
}

export const PATHS = {
  home: '/',
  floorPlans: pagePath('floor-plans') || '/floor-plans',
  interactiveMap: pagePath('interactive-map') || '/interactive-map',
  amenities: pagePath('amenities') || '/amenities',
  gallery: pagePath('gallery') || '/gallery',
  virtualTours: pagePath('virtual-tours') || '/virtual-tours',
  petPolicy: pagePath('pet-policy') || '/pet-policy',
  neighborhood: pagePath('neighborhood') || '/neighborhood',
  whyQuinn: pagePath('why-quinn') || '/why-quinn',
  reviews: pagePath('reviews') || '/reviews',
  faqs: pagePath('faqs') || '/faqs',
  feeGuide: pagePath('easy-fee-guide') || '/easy-fee-guide',
  contact: pagePath('contact') || '/contact-us',
  selfTour: pagePath('self-guided-tour') || '/schedule-a-tour',
  privacy: pagePath('privacy-policy') || '/privacy-policy',
  accessibility: pagePath('accessibility') || '/accessibility',
  sitemap: pagePath('sitemap') || '/sitemap',
};

export interface Review {
  rating?: string | number;
  name?: string;
  text?: string;
  date?: string;
  source?: string;
}

/** Four-star-and-up reviews with text, newest first. */
export function topReviews(reviews: Review[] | undefined): Review[] {
  return (reviews || [])
    .filter((r) => clean(r.text) && (num(r.rating) ?? 0) >= 4)
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

export interface SiteFlags {
  phone: string;
  applyUrl: string | null;
  portalUrl: string | null;
  selfTourUrl: string | null;
  hasFees: boolean;
  hasFaqs: boolean;
  hasReviews: boolean;
  hasVirtualTours: boolean;
  hasSightmap: boolean;
  /** A page alias is linkable: it exists, is visible, and its data is there. */
  shows: (alias: string) => boolean;
  /** False when a data-driven page (fees, FAQs, reviews, tours, map) has nothing to show. */
  hasData: (alias: string) => boolean;
}

export function useSiteFlags(): SiteFlags {
  const p = useRawProperty();
  const phone = formatPhone(usePropertyPhone());
  const selfTourUrl = useCustomField('custom_self_guided_tour_url');
  const sightmap = useCustomField('custom_engrain_sightmap_url');
  const hasFees = !!(clean(p.property_application_fee) || clean(p.property_admin_fee) || clean(p.property_fees_deposit_info));
  const hasFaqs = ((p.faqs as unknown[]) || []).length > 0;
  const hasReviews = topReviews(p.reviews as Review[]).length > 0;
  const hasVirtualTours = gatherTours(p).length > 0;
  const hasSightmap = !!sightmap;
  const dataFor: Record<string, boolean> = {
    'easy-fee-guide': hasFees,
    faqs: hasFaqs,
    reviews: hasReviews,
    'virtual-tours': hasVirtualTours,
    'interactive-map': hasSightmap,
  };
  return {
    phone,
    applyUrl: clean(p.property_apply_now_url) || clean(p.property_online_leasing_url),
    portalUrl: clean(p.property_resident_portal_url),
    selfTourUrl,
    hasFees,
    hasFaqs,
    hasReviews,
    hasVirtualTours,
    hasSightmap,
    shows: (alias) => pagePath(alias) != null && dataFor[alias] !== false,
    hasData: (alias) => dataFor[alias] !== false,
  };
}

/**
 * Tour CTAs point at the on-site self-guided tour page. A property with no self-guided tour URL
 * gets a Contact Us link instead, unless an editor pointed the CTA somewhere else.
 */
export function tourCta(
  cta: { url?: string | null; label?: string | null },
  selfTourUrl: string | null,
  defaults = { url: PATHS.selfTour, label: 'Book a Tour' },
): { href: string; label: string; isTour: boolean } {
  const url = cta.url || defaults.url;
  if (!selfTourUrl && url === defaults.url) return { href: PATHS.contact, label: 'Contact Us', isTour: false };
  return { href: url, label: cta.label || defaults.label, isTour: true };
}

/** Smooth-scroll a section into view under the sticky header (instant with reduced motion). */
export const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
};
